import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { TextInput, SelectInput, NumericInput, Bucket, BucketHeader, Checkbox } from "~/core";
import { actions as picklistActions, selectors as picklistSelectors } from "~/core/picklist";
import {
    getPickListCode,
    PICKLIST_CROPPING_SEASON,
    PICKLIST_CROP_PURPOSE,
    PICKLIST_IRRIGATION_TYPE,
} from "~/core/picklist/picklist-names";

import { IAnalaysisManagementArea } from "~/recs-events/analysis/model";
import { EventAreaIcon } from "~/action-panel/components/common/rec-event-info/icons/event-area";

import * as actions from "./actions";
import * as selectors from "./selectors";
import { analysisSelectors } from "~/recs-events";
import { getSetValuesForErrorCodeList } from "~/action-panel/components/common/validation-utils";
import { FormattingHelpers, ISelectOption } from "@ai360/core";
import { messages } from "../i18n-messages";

import { AnalysisIcon } from "./icons/analysis";

import "~/action-panel/components/common/rec-event-info/rec-event-info.css";
import "./analysis-info.css";
import "./analysis-management-area-form.css";
import { IErrorCodeList } from "./interfaces";

import { AgvanceAPI } from "@ai360/core";
import { useAppDispatch, useAppSelector } from "~/store/hooks";
import { getCustomerFieldMappings } from "~/customer-data/selectors";
import {
    stampCropSelectable,
    toCropDropdownOptions,
    useCropList,
} from "~/utils/crops/use-crop-list";
import { IS_SELECTABLE } from "~/utils/products/use-product-list";
import { messages as globalMessages } from "~/i18n-messages";

export const formLabelMessage = messages.managementAreaSidebar;
export const formLabelIcon = AnalysisIcon;

const errorCodeToMessageIdSetMap = new Map([
    [2806, messages.analysisDuplicateName],
    [2807, messages.analysisManagementAreaTypeNotSelected],
    [2808, messages.analysisManagementAreaDetailsMissing],
]);

export const errorCodesApply = (errorCodeList: IErrorCodeList): number => {
    return errorCodeList.some((errorCode) => errorCodeToMessageIdSetMap.has(errorCode));
};

const cropAttributes = new Set(["Crop", "Crop 2"]);

const cropPurposeAttributes = new Set(["Crop Purpose", "Crop Purpose 2"]);

const cropChemistryAttributes = new Set(["Crop Chemistry", "Crop Chemistry 2"]);

const irrigationTypeAttributes = new Set(["Irrigation Type"]);

const selectAttributes = new Set([
    ...cropAttributes,
    ...cropPurposeAttributes,
    ...irrigationTypeAttributes,
    ...cropChemistryAttributes,
]);

type AnalysisManagementAreaFormProps = {
    activeEditAnalysisLayerGuid: string;
    analysisDetailsErrorCodeList: number[];
};

const AnalysisManagementAreaForm_ = ({
    analysisDetailsErrorCodeList,
}: AnalysisManagementAreaFormProps): ReactElement => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();
    const croppingSeasonOptions = useAppSelector(
        (state) =>
            picklistSelectors.getPicklistOptionsFromCode(
                state,
                getPickListCode(PICKLIST_CROPPING_SEASON)
            ) as ISelectOption<string>[]
    );
    const irrigationTypeOptions = useAppSelector((state) =>
        picklistSelectors
            .getPicklistOptionsFromCode(state, getPickListCode(PICKLIST_IRRIGATION_TYPE))
            .map((o) => ({
                label: o.label,
                value: o.label,
            }))
    );
    const cropPurposeOptions = useAppSelector((state) =>
        picklistSelectors
            .getPicklistOptionsFromCode(state, getPickListCode(PICKLIST_CROP_PURPOSE))
            .map((o) => ({
                label: o.label,
                value: o.label,
            }))
    );
    const analysisSummary = useAppSelector(selectors.getAnalysisLayerInfo);
    const fieldGuid = analysisSummary?.layers[0].fieldGuid;
    const fieldMappings = useAppSelector(getCustomerFieldMappings);
    const customerGuid = fieldMappings.find((f) => f.fieldGuid === fieldGuid)?.customerGuid || "";
    const { data: cropChemistryData = [] } = AgvanceAPI.useGetAgvanceCropChemistryQuery(
        { customerGuid },
        { skip: !fieldGuid }
    );
    const { crops } = useCropList({ customerGuid, isEnabled: !!fieldGuid });
    const cropData = useMemo(() => toCropDropdownOptions(crops), [crops]);
    const currentArea = useAppSelector((state) =>
        analysisSelectors.getCurrentAnalysisArea(state, analysisSummary.analysisLayerAreaList)
    );
    const currentManagementAreaAttributes = useAppSelector((state) =>
        currentArea
            ? selectors.getManagementAreaAttributesByAreaId(state, currentArea.analysisLayerAreaId)
            : null
    );
    const managementAreaTypeOptions = useAppSelector(selectors.getManagementAreaTypes);
    const activeEditAnalysisLayerGuid = useAppSelector(selectors.getActiveSingleEditLayerGuid);

    const [errorMessagePlaceholderSet, setErrorMessagePlaceholderSet] = useState(() =>
        getSetValuesForErrorCodeList(analysisDetailsErrorCodeList, errorCodeToMessageIdSetMap)
    );

    const fetchManagementAreaAttributes = useCallback(
        (managementAreaTemplateGuid: string) =>
            dispatch(actions.fetchManagementAreaAttributes(managementAreaTemplateGuid)),
        [dispatch]
    );

    const fetchPicklistData = useCallback(
        (pickLists: Record<string, any>) => dispatch(picklistActions.fetchPicklistData(pickLists)),
        [dispatch]
    );

    const onUpdateCurrentAnalysisSummary = useCallback(
        (newProps: Partial<IAnalaysisManagementArea>) =>
            dispatch(actions.updateManagementAreaAnalysisSummary(newProps)),
        [dispatch]
    );

    const setManagementAreaAttributeData = useCallback(
        (
            areaId: number,
            name: string,
            textValue: string,
            yesNoValue: boolean,
            decimalIntValue: number
        ) =>
            dispatch(
                actions.updateManagementAreaAttributeData(
                    areaId,
                    name,
                    textValue,
                    yesNoValue,
                    decimalIntValue
                )
            ),
        [dispatch]
    );

    useEffect(() => {
        if (!croppingSeasonOptions || croppingSeasonOptions.length === 0) {
            fetchPicklistData({
                [PICKLIST_CROPPING_SEASON]: getPickListCode(PICKLIST_CROPPING_SEASON),
            });
        }
        if (!irrigationTypeOptions || irrigationTypeOptions.length === 0) {
            fetchPicklistData({
                [PICKLIST_IRRIGATION_TYPE]: getPickListCode(PICKLIST_IRRIGATION_TYPE),
            });
        }
        if (!cropPurposeOptions || cropPurposeOptions.length === 0) {
            fetchPicklistData({
                [PICKLIST_CROP_PURPOSE]: getPickListCode(PICKLIST_CROP_PURPOSE),
            });
        }
    }, [dispatch]);

    useEffect(() => {
        const errorMessagePlaceholderSet = getSetValuesForErrorCodeList(
            analysisDetailsErrorCodeList,
            errorCodeToMessageIdSetMap
        );
        setErrorMessagePlaceholderSet(errorMessagePlaceholderSet);
    }, [analysisDetailsErrorCodeList]);

    const updateAnalysisSummary = useCallback(
        (newProps: Partial<IAnalaysisManagementArea>) => {
            onUpdateCurrentAnalysisSummary(newProps);
        },
        [onUpdateCurrentAnalysisSummary]
    );

    const updateManagementAreaTemplateGuid = useCallback(
        (managementAreaTemplateGuid: string) => {
            fetchManagementAreaAttributes(managementAreaTemplateGuid);
            updateAnalysisSummary({ managementAreaTemplateGuid });
        },
        [fetchManagementAreaAttributes, updateAnalysisSummary]
    );

    const renderErrors = useCallback((errorMessagePlaceholderSet: Set<any>, formatMessage: any) => {
        return errorMessagePlaceholderSet == null || errorMessagePlaceholderSet.size === 0
            ? null
            : [...errorMessagePlaceholderSet].map((message, index) => (
                  <div className="analysis-error-message" key={index}>
                      {formatMessage(message)}
                  </div>
              ));
    }, []);

    const renderZoneNameAndSize = useCallback((): JSX.Element => {
        const areaId = currentArea?.analysisLayerAreaId;
        let calculatedArea =
            currentArea?.calculatedArea || analysisSummary.analysisLayerAreaList[0]?.calculatedArea;
        calculatedArea =
            typeof calculatedArea === "string" ? parseFloat(calculatedArea) : calculatedArea;

        if (activeEditAnalysisLayerGuid) {
            const editingAnalysisLayer = analysisSummary.analysisLayerAreaList.find(
                (area) => area.analysisLayerGuid === activeEditAnalysisLayerGuid
            );
            if (editingAnalysisLayer) {
                calculatedArea = editingAnalysisLayer.calculatedArea;
            }
        }

        const zoneName =
            analysisSummary.analysisLayerAreaList.length === 1
                ? formatMessage(messages.managementAreaDetailZoneNameEntireField)
                : formatMessage(messages.managementAreaDetailZoneName, {
                      areaId,
                  });
        const zoneSize = formatMessage(messages.managementAreaDetailZoneSize, {
            calculatedArea: FormattingHelpers.formatNumber(calculatedArea),
        });

        return (
            <div className="zone-info-heading">
                <EventAreaIcon className="zone-icon" />
                <div className="zone-name">{zoneName}</div>
                <div className="zone-size">{zoneSize}</div>
            </div>
        );
    }, [
        currentArea,
        analysisSummary.analysisLayerAreaList,
        activeEditAnalysisLayerGuid,
        formatMessage,
    ]);

    const updateAttributeData = useCallback(
        (name: string, textValue: string, yesNoValue: boolean, decimalIntValue: number): void => {
            if (!currentArea) return;
            const { analysisLayerAreaId } = currentArea;
            setManagementAreaAttributeData(
                analysisLayerAreaId,
                name,
                textValue,
                yesNoValue,
                decimalIntValue
            );
        },
        [currentArea, setManagementAreaAttributeData]
    );

    const renderManagementAreaAttributes = useCallback((): JSX.Element[] => {
        if (!currentManagementAreaAttributes) return [];

        return currentManagementAreaAttributes
            .sort((a, b) => a.columnOrder - b.columnOrder)
            .map((attribute) => {
                if (selectAttributes.has(attribute.name)) {
                    let selectOptions = [];
                    if (cropAttributes.has(attribute.name)) {
                        // Value is the crop *name*, not guid (pre-existing, unrelated to Agvance
                        // linkage) -- isSelectable is stamped from the same row before the name-only
                        // projection, so no separate guid-keyed lookup is needed.
                        selectOptions = stampCropSelectable(cropData, formatMessage).map((c) => ({
                            label: c.name,
                            value: c.name,
                            activeYn: c.activeYn,
                            isSelectable: c.isSelectable,
                            getToolTip: c.getToolTip,
                        }));
                    } else if (irrigationTypeAttributes.has(attribute.name)) {
                        selectOptions = irrigationTypeOptions;
                    } else if (cropPurposeAttributes.has(attribute.name)) {
                        selectOptions = cropPurposeOptions;
                    } else if (cropChemistryAttributes.has(attribute.name)) {
                        selectOptions = cropChemistryData.map((c) => ({
                            label: c.name,
                            value: c.name,
                            activeYn: true,
                        }));
                        const existingValueMatching = cropChemistryData.find(
                            (c) => c.name === attribute.textValue
                        );
                        if (attribute.textValue && !existingValueMatching) {
                            selectOptions.unshift({
                                label: attribute.textValue,
                                value: attribute.textValue,
                                activeYn: false,
                            });
                        }
                    }

                    // Value is the crop *name*, not guid (see the comment above) -- look up the
                    // selected crop by name, not by the shared guid-based isCropUnlinked utility.
                    const isSelectedCropUnlinked =
                        cropAttributes.has(attribute.name) &&
                        attribute.textValue != null &&
                        cropData.find((q) => q.name === attribute.textValue)?.isAgvanceLinked ===
                            false;
                    const cropNotLinkedText = isSelectedCropUnlinked
                        ? formatMessage(globalMessages.cropNotLinkedInAgvance)
                        : undefined;

                    return (
                        <SelectInput
                            optionIsHiddenKey={
                                cropAttributes.has(attribute.name)
                                    ? IS_SELECTABLE
                                    : cropChemistryAttributes.has(attribute.name)
                                    ? "activeYn"
                                    : undefined
                            }
                            key={attribute.importAttributeGuid}
                            disabled={false}
                            clearable={true}
                            containerClassNames={[
                                "analysis-layer-profit-loss-input",
                                { "select-form-input-error": isSelectedCropUnlinked },
                            ]}
                            onChange={(value) =>
                                updateAttributeData(attribute.name, value, null, null)
                            }
                            options={selectOptions}
                            placeholderText={attribute.name}
                            toolTip={cropNotLinkedText}
                            value={attribute.textValue}
                            testId={`analysis-mgmt-area-attribute-select-${attribute.importAttributeGuid}`}
                        />
                    );
                }

                switch (attribute.dataType) {
                    case "boolean":
                        return (
                            <Checkbox
                                key={attribute.importAttributeGuid}
                                className="analysis-layer-management-area-radio-button"
                                label={attribute.name}
                                value={!!attribute.yesNoValue}
                                onChange={(event, value) =>
                                    updateAttributeData(attribute.name, null, value, null)
                                }
                                testId={`analysis-mgmt-area-attribute-checkbox-${attribute.importAttributeGuid}`}
                            />
                        );
                    case "integer":
                        return (
                            <NumericInput
                                key={attribute.importAttributeGuid}
                                scale={0}
                                precision={9}
                                containerClassNames={["analysis-layer-management-area-input"]}
                                onChange={(value) =>
                                    updateAttributeData(attribute.name, null, null, value)
                                }
                                placeholderText={attribute.name}
                                value={attribute.decimalIntValue}
                                testId={`analysis-mgmt-area-attribute-integer-input-${attribute.importAttributeGuid}`}
                            />
                        );
                    case "decimal":
                        return (
                            <NumericInput
                                key={attribute.importAttributeGuid}
                                scale={2}
                                precision={9}
                                containerClassNames={["analysis-layer-management-area-input"]}
                                onChange={(value) =>
                                    updateAttributeData(attribute.name, null, null, value)
                                }
                                placeholderText={attribute.name}
                                value={attribute.decimalIntValue}
                                testId={`analysis-mgmt-area-attribute-decimal-input-${attribute.importAttributeGuid}`}
                            />
                        );
                    case "text":
                    default:
                        return (
                            <TextInput
                                key={attribute.importAttributeGuid}
                                required={false}
                                containerClassNames={["analysis-layer-management-area-input"]}
                                onChange={(value) =>
                                    updateAttributeData(attribute.name, value, null, null)
                                }
                                placeholderText={attribute.name}
                                value={attribute.textValue}
                                maxLength={50}
                                testId={`analysis-mgmt-area-attribute-text-input-${attribute.importAttributeGuid}`}
                            />
                        );
                }
            });
    }, [
        currentManagementAreaAttributes,
        cropData,
        irrigationTypeOptions,
        cropPurposeOptions,
        updateAttributeData,
    ]);

    const typeDropdownDisabled =
        analysisSummary.layers.length > 0 && !!analysisSummary.layers[0].analysisLayerGuid;
    const fieldGuids =
        analysisSummary.layers.length > 0
            ? [...new Set(analysisSummary.layers.map((l) => l.fieldGuid))]
            : [];

    return (
        <div className="rec-event-info-form">
            {renderErrors(errorMessagePlaceholderSet, formatMessage)}
            <Bucket
                showSymbol={false}
                isCollapsible={false}
                isExpanded
                testId="analysis-mgmt-area-general-bucket"
            >
                <BucketHeader className="analysis-info-bucket-header">
                    {formatMessage(messages.profitLossGeneralInformation)}
                </BucketHeader>
                <div className="analysis-layer-management-area-grid">
                    <TextInput
                        required={true}
                        autoFocus={!analysisSummary.name}
                        containerClassNames={["analysis-layer-management-area-input"]}
                        onChange={(v) => updateAnalysisSummary({ name: v })}
                        placeholderText={formatMessage(messages.managementAreaNamePlaceholder)}
                        value={analysisSummary.name}
                        maxLength={50}
                        testId="analysis-mgmt-area-name-input"
                    />
                    <SelectInput
                        disabled={false}
                        clearable={true}
                        required={false}
                        containerClassNames={["analysis-layer-management-area-input"]}
                        onChange={(v) =>
                            updateAnalysisSummary({
                                croppingSeasonGuid: v,
                            })
                        }
                        options={croppingSeasonOptions}
                        placeholderText={formatMessage(messages.managementAreaSeasonPlaceholder)}
                        value={analysisSummary.croppingSeasonGuid}
                        testId="analysis-mgmt-area-season-select"
                    />
                    <SelectInput
                        disabled={typeDropdownDisabled}
                        clearable={false}
                        required={true}
                        containerClassNames={["analysis-layer-management-area-type-input"]}
                        onChange={(v) => updateManagementAreaTemplateGuid(v)}
                        options={managementAreaTypeOptions}
                        placeholderText={formatMessage(
                            messages.managementAreaManagementAreaTypePlaceholder
                        )}
                        value={analysisSummary.managementAreaTemplateGuid}
                        testId="analysis-mgmt-area-type-select"
                    />
                </div>
            </Bucket>
            {currentArea ? (
                <>
                    {fieldGuids.length === 1 || activeEditAnalysisLayerGuid !== null ? (
                        <Bucket
                            showSymbol={false}
                            isCollapsible={false}
                            isExpanded
                            testId="analysis-mgmt-area-zone-name-bucket"
                        >
                            <BucketHeader className="analysis-info-bucket-header">
                                {renderZoneNameAndSize()}
                            </BucketHeader>
                        </Bucket>
                    ) : null}
                    <Bucket
                        showSymbol={false}
                        isCollapsible={false}
                        isExpanded
                        testId="analysis-mgmt-area-detail-bucket"
                    >
                        <BucketHeader className="analysis-info-bucket-header">
                            {formatMessage(messages.managementAreaDetail)}
                        </BucketHeader>
                        <div className="analysis-layer-management-area-grid">
                            {renderManagementAreaAttributes()}
                        </div>
                    </Bucket>
                </>
            ) : (
                <div className="no-zone-selected">
                    {formatMessage(messages.managementAreaNoZoneSelected)}
                </div>
            )}
        </div>
    );
};

export const AnalysisManagementAreaForm = AnalysisManagementAreaForm_;
